import { shallowClientDAO } from '@niarab2c/frontend-commons/src/daos';
import hotelDetailsDAO from '@niarab2c/frontend-commons/src/daos/hotelDetailsDAO';
import type { EngineRuleVersion, EntitiesDataProps, GetHotelAvailResponse, HotelDetails, HotelResult, HotelsAvailabilityPayload, Occupancy, OriginalHotelResult, Performance } from '@niarab2c/frontend-commons/src/types/hotels';
import callApi from '@niarab2c/frontend-commons/src/util/callApi';
import { createAsyncThunk, createSlice, unwrapResult, type AnyAction, type PayloadAction, type ThunkDispatch } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { parallelLimit, type AsyncFunction } from 'async';
import { format } from 'date-fns';
import _pick from 'lodash/pick';
import _times from 'lodash/times';
import _uniq from 'lodash/uniq';
import _uniqWith from 'lodash/uniqWith';
import { nanoid } from 'nanoid';
import { type ShallowClient } from 'niara-spear-crudmodel';
import defaultLocale from '@niaratech/niara-react-components/lib/util/defaultLocale';
import { mergeHotelResults, stringifyOccupancy } from '../../../util/hotels';
import { prepareHotelResult } from '../../../util/hotels/prepareHotelResult';
import { type BaseRootState, type CompleteRootState } from '../../base';
import * as Addon from '../addon';
import * as ShoppingCart from '../shoppingCart';
import * as QuotationRoomRates from '../quotationRoomRates';
export const EXPECTED_ROOMS_COUNT_KEY = 'otabuilder-expectedRoomsCount';
const isSameOccupancy = (a: Occupancy, b: Occupancy): boolean => (a && stringifyOccupancy(a)) == (b && stringifyOccupancy(b));
export type CriteriaForm = {
  startDate: string;
  endDate: string;
  rooms: Array<{
    adults: number;
    children?: number;
    childrenAges?: number[];
  }>;
  destinations?: {
    propertyId?: string;
    contentType?: 'property' | 'region';
    cityIds?: Array<string>;
    hotelIds?: Array<string>;
  };
  destinationName?: string | Array<string>;
  bestOnly?: boolean;
  promoCode?: string;
  couponCode?: string;
  credentialIds?: string[];
  availRatesOnly?: boolean;
  distributionIds?: string[];
  roomRateIds?: string[];
  clientId?: string;
  clientName?: string;
  enablePromoCode?: boolean;
  isThirdPartyReservation?: boolean;
  quotationId?: string;
  personId?: string; // usado apenas para front unificado
  personName?: string;
  previousOrderId?: string;
  user?: any;
  personTSER_idContrato?: number;
  roomTypeId?: string;
  ratePlanId?: string;
  expectedRoomsCount?: number;
  awards?: number[];
};
export interface State {
  criteria?: CriteriaForm;
  start?: number;
  end?: number;
  status?: 'searching' | 'idle' | 'complete' | 'error';
  errorMessage?: string;
  searchId?: string;
  results?: Record<string, HotelResult>;
  referencePoint?: {
    latitude: number;
    longitude: number;
  };
  rawDistanceDisabled?: boolean;
  entitiesData?: Record<string, EntitiesDataProps>;
  engineRuleVersion?: EngineRuleVersion;
  isForGroups?: boolean;
  messages?: GetHotelAvailResponse['messages'];
  performance?: Performance[];
  locale?: string;
  client?: ShallowClient;
}
type GetHotelsResultsTypeParamsType = {
  criteriaForm: CriteriaForm;
  limit: number | null;
  offset: number | null;
  occupancy: Occupancy;
  searchId: string;
  roomRatesLimit?: number | null;
  clientId: string;
  landingPageLocator: string;
  quotationToken: string;
  quotationIndex?: number;
  selfBooking: boolean;
  b2c: boolean;
  personBalance?: number;
  personContractStatus?: string;
  manualReservationId?: string;
  personTSER_idContrato?: number;
  pointsToBurn: number | null;
  loyaltyRedeemId: string | null;
  lateCurrencyConversion?: boolean;
  isLoyaltyOptIn?: boolean;
  isForGroups?: boolean;
  locale?: string;
} & Required<Pick<HotelsAvailabilityPayload, 'purchaseSessionId' | 'engineRuleVersion' | 'discountedClientCommissionPercentage' | 'discountedMarkupPercentage' | 'paymentOptionId' | 'paymentOptionType' | 'analyticsEventType'
// | 'isThirdPartyReservation' | 'couponCode'  /* estão no CriteriaForm */
>>;
export type SearchHotelsResponse = {
  results: HotelResult[];
  searchId: string;
  errorMessage?: string;
  locale?: string;
};
export const searchHotels = createAsyncThunk<SearchHotelsResponse, {
  criteriaForm: CriteriaForm;
  searchId: string;
  maxPropertyCnt?: number;
  roomRatesLimit?: number;
  engineRuleVersion;
  manualReservationId?: string;
  quotationToken?: string;
  quotationIndex?: number;
  discountedClientCommissionPercentage?: number;
  discountedMarkupPercentage?: number;
  /**
   * @deprecated
   */
  paymentOptionId?: string;
  paymentOptionType?: string;
  analyticsEventType?: null | 'hotelAvailability';
  loyaltyRedeemId?: string;
  pointsToBurn?: number;
  lateCurrencyConversion?: boolean;
  isLoyaltyOptIn?: boolean;
  isForGroups?: boolean;
  locale?: string;
}, {
  state: CompleteRootState;
  rejectValue: {
    searchId: string;
    errorMessage: string;
  };
}>('hotelSearch/searchHotels', async ({
  criteriaForm,
  searchId,
  maxPropertyCnt,
  roomRatesLimit,
  engineRuleVersion,
  manualReservationId,
  quotationToken,
  quotationIndex,
  discountedClientCommissionPercentage,
  discountedMarkupPercentage,
  paymentOptionId,
  paymentOptionType,
  analyticsEventType,
  pointsToBurn,
  loyaltyRedeemId,
  lateCurrencyConversion,
  isLoyaltyOptIn,
  isForGroups,
  locale = defaultLocale()
}, {
  getState,
  dispatch,
  rejectWithValue
}) => {
  const rootState = getState();
  const landingPageLocator = rootState.storefrontConfig?.storefront?.locator;
  const b2c = rootState.authentication?.b2c;
  const selfBooking = b2c || rootState.authentication?.isClientUser;
  const personTSER_idContrato = criteriaForm?.personTSER_idContrato ?? rootState.shoppingCart?.personTSER_idContrato ?? rootState.loyalty.TSER_idContrato;

  // no ota-builder, o clientId está no reducer
  // no cotação, o clientId está no reducer
  // no front-unificado, o clientId virá do criteriaForm
  const clientId = criteriaForm?.clientId ?? rootState.quotation?.quotation?.clientId ?? rootState.core.clientId;
  const purchaseSessionId = rootState.shoppingCart?.purchaseSessionId;
  const tenant = rootState?.core?.tenant;
  const client = rootState?.core?.client ?? (clientId ? await shallowClientDAO.findById(clientId) : null);
  dispatch(QuotationRoomRates.setClient({
    client
  }));
  const mandatory_engineRuleVersion = client?.engineRule_enforceVersion_deleteme != null ? client?.engineRule_enforceVersion_deleteme : tenant.distribution_disabled ? '1' : null;
  if (mandatory_engineRuleVersion != null && mandatory_engineRuleVersion != engineRuleVersion) {
    return rejectWithValue({
      errorMessage: engineRuleVersion == '1' ? "Novo motor incompatível com instalação/cliente" : "Motor com distribuição incompatível com instalação/cliente",
      searchId
    });
  }

  // personId escolhido nos critérios do front unificado
  const personId = b2c ? rootState?.authentication?.user?.personId : criteriaForm?.personId;
  if (b2c && criteriaForm?.personId && criteriaForm?.personId != rootState?.authentication?.user?.personId) {
    criteriaForm.personId = rootState?.authentication?.user?.personId;
  }
  let personBalance = null;
  let personContractStatus = null;
  if (!b2c) {
    await dispatch(ShoppingCart.setPerson({
      personId,
      clientId,
      personTSER_idContrato,
      _dontRefreshBalance: true
    }));
    const refreshBalanceResponse = await dispatch(ShoppingCart.refreshPersonBalance({
      personId,
      clientId,
      personTSER_idContrato
    })).then(unwrapResult).catch(() => null);
    personBalance = refreshBalanceResponse?.personBalance;
    personContractStatus = refreshBalanceResponse?.personContractStatus;
  }
  if (!criteriaForm?.startDate) {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa, não há data de check-in",
      searchId
    });
  }
  if (!criteriaForm?.endDate) {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa, não há data de check-out",
      searchId
    });
  }
  if (!b2c && personContractStatus && personContractStatus !== 'ACTIVE') {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa, contrato com status inválido",
      searchId
    });
  }

  // data de acordo com o timezone do usuário, não UTC
  // ver bug #165982
  const TODAY_YYYY_MM_DD = format(Date.now(), 'yyyy-MM-dd');
  if (criteriaForm.startDate < TODAY_YYYY_MM_DD) {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa, check-in no passado",
      searchId
    });
  }
  if (criteriaForm.startDate > criteriaForm.endDate) {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa, data de check-in após a data de check-out",
      searchId
    });
  }
  if (criteriaForm?.startDate === criteriaForm?.endDate) {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa, data de check-in igual à data de check-out",
      searchId
    });
  }
  if (criteriaForm?.rooms[0]?.children > 0 && criteriaForm?.rooms[0]?.children !== criteriaForm?.rooms[0]?.childrenAges.length) {
    return rejectWithValue({
      errorMessage: "Erro na pesquisa: o número de crianças não corresponde à quantidade de idades informadas",
      searchId
    });
  }

  // if (quotationToken) {
  //   //TODO: como fazer com cotação?
  //   return {
  //     results: undefined,
  //     searchId,
  //   }
  // }

  dispatch(clearMessagesAvailResponse());
  if (tenant?.modules_addons) {
    dispatch(Addon.loadAddonsRule({
      clientId
    }));
  }
  const _isForGroups = rootState?.hotel?.isForGroups ?? isForGroups;
  try {
    const availRatesOnly = criteriaForm.availRatesOnly ?? true;
    const {
      destinations
    } = criteriaForm;
    return await Promise.all(_uniqWith(criteriaForm.rooms, isSameOccupancy).map(occupancy => {
      return async function (): Promise<SearchHotelsResponse> {
        if (destinations.propertyId && destinations.contentType) {
          // se a quantidade de hotéis for abaixo de 20, força pesquisa com bestOnly false
          // também não pesquisa com bestOnly se availRatesOnly == false

          let bestOnly = !availRatesOnly ? false : criteriaForm.bestOnly ?? true;

          // Era: (Deixamos limite de 5 porque retornou dados em 2 segundos e, com isso, exibimos em pouco tempo os dados das tarifas)
          // Mudei de 5 para 50 por causa do #230382
          const firstSearchLimit = 50;
          const errors: any[] = [];
          const paramsGetHotels: GetHotelsResultsTypeParamsType = {
            criteriaForm: {
              ...criteriaForm,
              availRatesOnly,
              bestOnly
            },
            limit: firstSearchLimit,
            occupancy,
            offset: 0,
            searchId,
            roomRatesLimit,
            clientId,
            landingPageLocator,
            quotationToken,
            quotationIndex,
            b2c,
            selfBooking,
            personBalance,
            personContractStatus,
            engineRuleVersion,
            manualReservationId,
            purchaseSessionId,
            discountedClientCommissionPercentage,
            discountedMarkupPercentage,
            personTSER_idContrato,
            paymentOptionId,
            paymentOptionType,
            analyticsEventType,
            pointsToBurn,
            loyaltyRedeemId,
            lateCurrencyConversion,
            isLoyaltyOptIn,
            isForGroups: _isForGroups,
            locale
          };
          const firstResults = await _getHotelsResults(paramsGetHotels, dispatch).then(r => {
            if (r?.hotelResults?.length > 0) {
              dispatch?.(updateHotelResults({
                results: r?.hotelResults,
                searchId
              }));
            }
            if (r?.performance?.length > 0) {
              dispatch?.(updatePerformance({
                performance: r?.performance,
                searchId
              }));
            }
            return r;
          }).catch(err => {
            errors.push(err);
          });
          const totalCount = firstResults ? firstResults?.totalCount : undefined;
          bestOnly = !availRatesOnly ? false : criteriaForm.bestOnly ?? totalCount > 20 ? true : false;
          const propertyLimit = maxPropertyCnt > 0 ? Math.min(maxPropertyCnt, totalCount) : Math.min(2500 /* quantidade de hotéis em Roma na Reservia em 2024-08-06: 2233 */, totalCount);
          const pageSize = !bestOnly ? 20 : 50; // limite de quantidade de hotéis por "página"
          const concurrencyLimit = 10; // limite escolhido arbitrariamente para buscas em paralelo

          // quantidade de páginas
          const PAGES = Math.ceil((propertyLimit - firstSearchLimit) / pageSize);
          const asyncFunctions: AsyncFunction<HotelResult[]>[] = [callback => {
            //Aqui inserimos os resultados da busca da primeira chamada da disponibilidade.
            callback(null, firstResults ? firstResults?.hotelResults : []);
          }, ..._times(PAGES, (index): AsyncFunction<HotelResult[]> => callback => {
            const offset = pageSize * index + firstSearchLimit;
            const paramsGetHotels: GetHotelsResultsTypeParamsType = {
              criteriaForm: {
                ...criteriaForm,
                bestOnly,
                availRatesOnly
              },
              limit: pageSize,
              occupancy,
              offset,
              searchId,
              roomRatesLimit,
              clientId,
              landingPageLocator,
              quotationToken,
              quotationIndex,
              b2c,
              selfBooking,
              personBalance,
              personContractStatus,
              engineRuleVersion,
              manualReservationId,
              purchaseSessionId,
              discountedClientCommissionPercentage,
              discountedMarkupPercentage,
              personTSER_idContrato,
              paymentOptionId,
              paymentOptionType,
              analyticsEventType: index === 0 ? analyticsEventType : undefined,
              pointsToBurn,
              loyaltyRedeemId,
              lateCurrencyConversion,
              isLoyaltyOptIn,
              isForGroups: _isForGroups,
              locale
            };
            return _getHotelsResults(paramsGetHotels, dispatch).then(r => {
              if (r?.hotelResults?.length > 0) {
                dispatch?.(updateHotelResults({
                  results: r?.hotelResults,
                  searchId
                }));
              }
              callback(null, r?.hotelResults);
              if (r?.performance?.length > 0) {
                dispatch?.(updatePerformance({
                  performance: r?.performance,
                  searchId
                }));
              }
              return r?.hotelResults;
            }).catch(err => {
              errors.push(err);
              callback(null, null);
            });
          })];
          return await new Promise<SearchHotelsResponse>((res, rej) => {
            parallelLimit(asyncFunctions, concurrencyLimit, (err, results) => {
              const successes = results?.filter(Boolean);
              if (successes?.length > 0) {
                const errorMessage = errors?.length > 0 ? _uniq(errors.map(err => typeof err == 'string' ? err : err?.message ?? err?.errorMessage ?? "Erro desconhecido").filter(Boolean)).join(', ') : undefined;
                res({
                  results: successes.flat(1),
                  errorMessage,
                  searchId
                });
              } else if (errors?.length > 0) {
                rej(errors?.[0]);
              } else {
                res({
                  results: [],
                  searchId
                });
              }
            });
          });
        } else {
          const paramsGetHotels: GetHotelsResultsTypeParamsType = {
            criteriaForm: {
              bestOnly: false,
              availRatesOnly,
              ...criteriaForm
            },
            limit: null,
            occupancy,
            offset: null,
            searchId,
            roomRatesLimit,
            landingPageLocator,
            clientId,
            quotationToken,
            quotationIndex,
            b2c,
            selfBooking,
            personBalance,
            personContractStatus,
            engineRuleVersion,
            manualReservationId,
            purchaseSessionId,
            discountedClientCommissionPercentage,
            discountedMarkupPercentage,
            personTSER_idContrato,
            paymentOptionId,
            paymentOptionType,
            analyticsEventType,
            pointsToBurn,
            loyaltyRedeemId,
            lateCurrencyConversion,
            isLoyaltyOptIn,
            isForGroups: _isForGroups,
            locale
          };
          return await _getHotelsResults(paramsGetHotels, dispatch).then(r => {
            if (r?.hotelResults?.length > 0) {
              dispatch?.(updateHotelResults({
                results: r?.hotelResults,
                searchId
              }));
            }
            if (r?.performance?.length > 0) {
              dispatch?.(updatePerformance({
                performance: r?.performance,
                searchId
              }));
            }
            return {
              results: r?.hotelResults,
              searchId,
              locale
            };
          });
        }
      }();
    })).then(responses => {
      return responses.reduce<SearchHotelsResponse>((acc, response) => {
        if (response.results?.length > 0) acc.results.push(...response.results);
        acc.errorMessage = _uniq([acc.errorMessage, response.errorMessage].filter(Boolean)).join(', ');
        acc.locale = response?.locale;
        return acc;
      }, {
        results: [],
        searchId
      });
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return rejectWithValue({
      errorMessage: error?.message || error?.errorMessage || typeof error == 'string' && error || "Erro desconhecido",
      searchId
    });
  }
});

/**
 *
 * @param param0
 * @returns
 */
const _getHotelsResults = async (params: GetHotelsResultsTypeParamsType, dispatch?: ThunkDispatch<CompleteRootState, unknown, AnyAction>): Promise<{
  hotelResults: HotelResult[];
  totalCount?: number;
  performance?: Performance[];
  locale?: string;
}> => {
  const {
    criteriaForm,
    limit,
    offset,
    occupancy,
    roomRatesLimit,
    searchId,
    clientId,
    landingPageLocator,
    selfBooking,
    b2c,
    personBalance,
    personContractStatus,
    engineRuleVersion,
    manualReservationId,
    quotationToken,
    quotationIndex,
    purchaseSessionId,
    discountedClientCommissionPercentage,
    discountedMarkupPercentage,
    personTSER_idContrato,
    paymentOptionId,
    paymentOptionType,
    analyticsEventType,
    loyaltyRedeemId,
    pointsToBurn,
    lateCurrencyConversion,
    isLoyaltyOptIn,
    isForGroups,
    locale = defaultLocale?.()
  } = params;
  const criteria: HotelsAvailabilityPayload['criteria'] = {
    ..._pick(criteriaForm, 'availRatesOnly', 'promoCode', 'limit', 'offset', 'credentialIds', 'bestOnly', 'distributionIds', 'roomRateIds', 'destinations', 'awards'),
    searchId,
    time: {
      endDate: criteriaForm.endDate,
      startDate: criteriaForm.startDate
    },
    destinationName: !criteriaForm.destinationName ? undefined : typeof criteriaForm.destinationName == 'string' ? criteriaForm.destinationName : criteriaForm.destinationName.join(', '),
    clientId: clientId ?? criteriaForm.clientId,
    clientName: criteriaForm?.clientName,
    landingPage: landingPageLocator,
    occupancy: {
      ...occupancy,
      childrenAges: occupancy.childrenAges ?? _times(occupancy.children ?? 0, () => 0),
      quantity: 1 // TODO
    },
    selfBooking,
    // NIT-1142
    b2c,
    // NIT-1142
    ratePlanId: criteriaForm?.ratePlanId,
    // NIT-4497
    roomTypeId: criteriaForm?.roomTypeId // NIT-4497
  };
  const apipath = landingPageLocator ? `/otabuilder/${landingPageLocator}/hotels/availability` : manualReservationId ? `/hotels/manual/${manualReservationId}/availability` : quotationToken ? `/quotationTokens/${quotationToken}/availability/${quotationIndex}` : '/hotels/availability';
  const payload: HotelsAvailabilityPayload = {
    isThirdPartyReservation: criteriaForm?.isThirdPartyReservation || false,
    buyer_personId: criteriaForm?.personId ?? undefined,
    engineRuleVersion,
    couponCode: criteriaForm?.couponCode,
    criteria: {
      ...criteria,
      limit,
      offset
    },
    purchaseSessionId,
    discountedClientCommissionPercentage,
    discountedMarkupPercentage,
    TSER_idContrato: personTSER_idContrato,
    paymentOptionId,
    paymentOptionType,
    analyticsEventType,
    pointsToBurn,
    loyaltyRedeemId,
    lateCurrencyConversion,
    isLoyaltyOptIn,
    isForGroups
  };
  return await callApi('niara-spear-booking', apipath, 'post', {
    body: payload,
    locale
  }).then((response: GetHotelAvailResponse) => {
    //Armazenando mensagens de erro
    if (response?.messages?.length > 0) {
      dispatch?.(addMessagesAvailResponse({
        messages: response.messages
      }));
    }
    //força o hotel id do resultado, se o critério veio com hotelId
    const hotelId = criteria?.destinations?.hotelIds?.length === 1 ? criteria?.destinations?.hotelIds[0] : null;

    //setar occupation para cada room rate recebido!
    const entitiesData = response.entitiesData;
    const preparedResults = response.hotels?.map(hotelResult => {
      if (hotelId) {
        //força o hotel id do resultado, se o critério veio com hotelId
        hotelResult['_id'] = hotelId;
      }
      if (roomRatesLimit > 0 && hotelResult.roomRates?.length > 0) {
        hotelResult.roomRates = hotelResult.roomRates.slice(0, roomRatesLimit);
      }
      if (roomRatesLimit > 0 && hotelResult.notAvailableRoomRates?.length > 0) {
        hotelResult.notAvailableRoomRates = hotelResult.notAvailableRoomRates.slice(0, roomRatesLimit);
      }
      delete hotelResult['debugRates'];
      return prepareHotelResult(hotelResult, undefined, {
        occupancy,
        entitiesData,
        promoCode: criteria.promoCode,
        clientId: criteria.clientId,
        selfBooking,
        b2c,
        personBalance,
        personContractStatus,
        engineRuleVersion,
        manualReservationId,
        quotationIndex,
        quotationToken,
        locale
      });
    });
    const performance = response?.metadata?.performance;
    return {
      hotelResults: preparedResults,
      totalCount: response?.totalCount,
      performance,
      locale
    };
  }).catch((err: Error) => {
    Sentry.captureException(new Error('Erro GetHotelAvail ' + err?.message, {
      cause: err
    }), {
      contexts: {
        getHotelResults: {
          path: apipath,
          payload
        }
      }
    });
    throw err;
  });
};

/**
 * Retoma o processo de checkout buscando um hotel específico e adicionando ao carrinho
 */
export const resumeCheckout = createAsyncThunk<any, {
  criteriaForms: CriteriaForm[];
  engineRuleVersion: '0' | '1';
  quantities?: number[]; // Quantidade de quartos para cada criteriaForm
  searchId: string;
}, {
  state: CompleteRootState;
  rejectValue: {
    searchId: string;
    errorMessage: string;
  };
}>('hotelSearch/resumeCheckout', async ({
  criteriaForms,
  engineRuleVersion,
  quantities,
  searchId
}, {
  getState,
  dispatch
}): Promise<{
  hotelResults: HotelResult[];
  searchId: string;
}> => {
  const storefront = getState().storefrontConfig?.storefront;
  const landingPageLocator = storefront?.locator;
  const b2c = getState().authentication?.b2c;
  const clientId = getState()?.storefrontConfig?.storefront?.clientId ?? criteriaForms?.[0]?.clientId;
  const buyerInfo = getState()?.shoppingCart?.personalData?.buyer ?? getState()?.authentication?.user;
  let mergedHotelResults = {} as Record<string, HotelResult>;
  if (!criteriaForms || criteriaForms?.length === 0) {
    return {
      hotelResults: [],
      searchId
    };
  }
  if (!b2c || !landingPageLocator) {
    return {
      hotelResults: [],
      searchId
    };
  }
  await dispatch(ShoppingCart.loadCheckoutFlowRule({
    clientId
  }));
  for (let idx = 0; idx < criteriaForms.length; idx++) {
    const criteriaForm = criteriaForms[idx];
    const occupancy = {
      ...criteriaForm?.rooms?.[0],
      quantity: 1
    };
    const paramsGetHotels = {
      criteriaForm,
      limit: 1,
      occupancy,
      offset: 0,
      searchId,
      roomRatesLimit: 1,
      clientId,
      landingPageLocator,
      b2c,
      engineRuleVersion,
      purchaseSessionId: nanoid(),
      analyticsEventType: 'hotelAvailability',
      discountedClientCommissionPercentage: undefined,
      discountedMarkupPercentage: undefined,
      paymentOptionId: undefined,
      paymentOptionType: undefined,
      loyaltyRedeemId: undefined,
      pointsToBurn: undefined,
      quotationToken: undefined,
      selfBooking: true
    } as GetHotelsResultsTypeParamsType;
    const {
      hotelResults
    } = await _getHotelsResults(paramsGetHotels, dispatch);
    if (hotelResults) {
      // pega o primeiro resultado
      const hotelResult = hotelResults?.[0];
      const roomRate = hotelResult?.roomRates?.[0];
      if (roomRate) {
        // não adiciona mais quartos do que o disponível
        const quantity = Math.min(quantities?.[idx], roomRate?.roomType?.units || 1);
        dispatch(ShoppingCart.setHotelRoomRatesQuantity({
          roomRates: [roomRate],
          hotelResult,
          quantity,
          storefront,
          criteria: criteriaForm,
          buyer: buyerInfo
        }));
      }
      mergedHotelResults = mergeHotelResults(hotelResults, mergedHotelResults);
    }
  }
  return {
    hotelResults: Object.values(mergedHotelResults),
    searchId
  };
});
const initialState: State = {};

/**
 * HotelSearch é feito para ser inserido em outro payload.
 * Atenção: todo action precisa ter searchId para funcionar corretamente!
 */
const searchSlice = createSlice({
  name: 'hotelSearch',
  initialState: initialState,
  reducers: {
    setSearchId: (state, action: PayloadAction<{
      searchId: string;
    }>) => {
      state.searchId = action.payload.searchId;
    },
    updateHotelResults: (state, action: PayloadAction<{
      results: Array<Partial<HotelResult | OriginalHotelResult>>;
      searchId: string;
    }>) => {
      if (state.searchId == action.payload.searchId) {
        const {
          results: hotels
        } = action.payload;
        const results = mergeHotelResults(hotels, state.results);
        // Reference Point será usado como centro do mapa se todos os resultados forem filtrados
        // TODO: ideal é colocar a posição central do destino, em caso de cidade
        const referencePoint = state.referencePoint ?? Object.values(results)?.find(r => r._hotelDetails?.location)?._hotelDetails?.location ?? null;
        return {
          ...state,
          results,
          referencePoint
        };
      }
      return state;
    },
    updatePerformance: (state, action: PayloadAction<{
      performance: Performance[];
      searchId: string;
    }>) => {
      if (state.searchId == action.payload.searchId) {
        const {
          performance: data
        } = action.payload;
        const newPerformance = data ?? [];
        const mergedPerformance = [...(state.performance ?? []), ...newPerformance];
        return {
          ...state,
          performance: mergedPerformance
        };
      }
      return state;
    },
    addMessagesAvailResponse: (state, action: PayloadAction<{
      messages?: GetHotelAvailResponse['messages'];
    }>) => {
      const messages = action?.payload?.messages ?? [];
      if (messages.length > 0) {
        const currentMessages = state?.messages ?? [];
        return {
          ...state,
          messages: currentMessages.concat(messages)
        };
      }
    },
    clearMessagesAvailResponse: (state: State) => {
      return {
        ...state,
        messages: []
      };
    }
  },
  extraReducers: builder => {
    builder.addCase(searchHotels.pending, (state, p) => {
      const arg = p.meta?.arg;
      if (!state.searchId || state.searchId == arg.searchId) {
        return {
          ...state,
          start: Date.now(),
          end: undefined,
          status: 'searching',
          searchId: arg.searchId,
          criteria: arg.criteriaForm,
          errorMessage: null,
          engineRuleVersion: arg.engineRuleVersion,
          performance: []
        };
      }
    }).addCase(searchHotels.fulfilled, (state, {
      payload
    }) => {
      if (state.searchId == payload.searchId) {
        state.status = 'complete';
        state.end = Date.now();
        if (payload.errorMessage) {
          state.errorMessage = payload.errorMessage;
        }
        if (payload?.locale) {
          state.locale = payload?.locale;
        }
        return state;
      } else {
        return state;
      }
    }).addCase(searchHotels.rejected, (state, {
      payload
    }) => {
      return {
        ...state,
        status: 'complete',
        errorMessage: payload?.errorMessage
      };
    }).addCase(resumeCheckout.pending, (state, p) => {
      const arg = p.meta?.arg;
      if (!state.searchId || state.searchId == arg.searchId) {
        return {
          ...state,
          start: Date.now(),
          end: undefined,
          status: 'searching',
          errorMessage: null,
          searchId: arg.searchId,
          engineRuleVersion: arg.engineRuleVersion
        };
      }
      return state;
    }).addCase(resumeCheckout.fulfilled, (state, p) => {
      const arg = p.meta?.arg;
      if (state.searchId == arg.searchId) {
        return {
          ...state,
          status: 'complete',
          end: Date.now()
        };
      }
      return state;
    }).addCase(resumeCheckout.rejected, (state, p) => {
      const arg = p.meta?.arg;
      if (state.searchId == arg.searchId) {
        return {
          ...state,
          status: 'error',
          end: Date.now(),
          errorMessage: 'Erro desconhecido'
        };
      }
      return state;
    });
  }
});
export default searchSlice.reducer;

// Action creators are generated for each case reducer function
const {
  updateHotelResults,
  addMessagesAvailResponse,
  clearMessagesAvailResponse,
  updatePerformance
} = searchSlice.actions;
export const setHotelDetails = (hotelResultId: string, hotelDetails: HotelDetails, searchId: string, hotelDetailsNotFound?: HotelResult['_hotelDetailsNotFound']): ReturnType<typeof updateHotelResults> => {
  return updateHotelResults({
    results: [{
      _id: hotelResultId,
      _hotelDetails: hotelDetails,
      _hotelDetailsHotelId: hotelDetails?.hotelId,
      _hotelDetailsInProgress: false,
      _hotelDetailsNotFound: hotelDetailsNotFound
    }],
    searchId
  });
};
export const loadHotelDetails = createAsyncThunk<{
  hotelDetails: HotelDetails[];
}, {
  results: Array<HotelResult>;
  searchId: string;
}, {
  state: BaseRootState;
}>('hotelSearch/loadHotelDetails', async ({
  results,
  searchId
}, {
  getState,
  dispatch
}) => {
  const tenantId = getState().core.tenantId;
  let shouldLoadNotEmpty = false;
  const x = results?.map<[boolean, HotelDetails, HotelResult]>(hotelResult => {
    const shouldLoad = !hotelResult._hotelDetails && !hotelResult._hotelDetailsInProgress && !hotelResult?._hotelDetailsNotFound;
    shouldLoadNotEmpty = shouldLoadNotEmpty || shouldLoad;
    return [shouldLoad, hotelResult._hotelDetails, hotelResult];
  });
  if (!shouldLoadNotEmpty) {
    return {
      hotelDetails: x.map(([, hotelDetails]) => hotelDetails)
    };
  }
  dispatch(updateHotelResults({
    results: x.filter(([shouldLoad]) => shouldLoad).map(([,, hotelResult]) => ({
      _id: hotelResult._id,
      _hotelDetailsInProgress: true
    })),
    searchId
  }));
  return Promise.all(x.map(([shouldLoad, hotelDetails, hotelResult]) => {
    if (!shouldLoad) return hotelDetails;

    // precisa carregar os detalhes do hotel
    const resultId = hotelResult._id;
    const hotelIdToSearch = hotelResult?.hotel?.id ?? hotelResult?.omniHotelId ?? hotelResult?._id; // prioriza o hotel details do hotel da busca e depois da omnibees
    const hotelDetailsUrl = hotelResult?.hotel?.detailsUrl;
    return hotelDetailsDAO.findById(hotelIdToSearch, tenantId, hotelResult.clientId, hotelDetailsUrl).catch(() => null).then(hotelDetails => {
      let hotelDetailsNotFound = false;
      if (!hotelDetails) {
        hotelDetailsNotFound = true;
      }
      dispatch(setHotelDetails(resultId, hotelDetails, searchId, hotelDetailsNotFound));
      return hotelDetails;
    });
  })).then(hotelDetails => {
    return {
      hotelDetails
    };
  });
});
export const hydrateHotelResults = createAsyncThunk<SearchHotelsResponse, {
  results: Pick<HotelResult, '_id' | 'hotel' | '_hydrationCompleted' | '_hydrationInProgress'>[];
  criteriaForm: CriteriaForm;
  searchId;
  engineRuleVersion: EngineRuleVersion;
  /**
   * @deprecated
   */
  paymentOptionId?: string;
  paymentOptionType?: string;
  locale?: string;
}, {
  state: CompleteRootState;
  rejectValue: {
    searchId: string;
    errorMessage: string;
  };
}>('hotelSearch/hydrateHotelResults', async ({
  results: _paramResults,
  criteriaForm,
  searchId,
  engineRuleVersion,
  paymentOptionId,
  paymentOptionType,
  locale
}, {
  dispatch,
  getState,
  rejectWithValue
}) => {
  const rootState = getState();
  const landingPageLocator = rootState.storefrontConfig?.storefront?.locator;
  const quotationToken = rootState.quotation?.quotation?.quotationToken;
  const clientId = rootState.core.clientId;
  const b2c = rootState.authentication?.b2c;
  const selfBooking = b2c || rootState.authentication?.isClientUser;
  const purchaseSessionId = rootState.shoppingCart?.purchaseSessionId;
  const isHydratable = (r: (typeof _paramResults)[number]) => !r._hydrationCompleted && !r._hydrationInProgress;
  const hydratableResults = _paramResults.filter(isHydratable);
  const loyaltyRedeemId = rootState.shoppingCart?.loyaltyRedeemId;
  const pointsToBurn = loyaltyRedeemId ? rootState.shoppingCart?.pointsToBurn : null;
  const isForGroups = rootState?.hotel?.isForGroups;
  if (quotationToken) {
    //TODO: como fazer com cotação?
    return {
      results: undefined,
      searchId
    };
  }

  // marca todos os resultados como _hydrationInProgress
  dispatch(updateHotelResults({
    results: hydratableResults.map(r => ({
      _id: r._id,
      _hydrationInProgress: true
    })),
    searchId
  }));
  const errors: any[] = [];
  const availRatesOnly = criteriaForm.availRatesOnly ?? true;
  const asyncFunctions: AsyncFunction<HotelResult[]>[] = _paramResults.map(hr => callback => {
    if (!isHydratable(hr)) {
      // resultados já hidratados - retorna o próprio resultado
      const r = [hr as HotelResult];
      callback(null, r);
      return Promise.resolve(r);
    } else {
      return Promise.all(criteriaForm.rooms.map(occupancy => {
        return async function () {
          const paramsGetHotels: GetHotelsResultsTypeParamsType = {
            criteriaForm: {
              bestOnly: false,
              availRatesOnly,
              ...criteriaForm,
              destinations: {
                hotelIds: [hr._id]
              }
            },
            limit: null,
            occupancy,
            offset: null,
            searchId,
            clientId,
            landingPageLocator,
            quotationToken,
            b2c,
            selfBooking,
            engineRuleVersion,
            purchaseSessionId,
            discountedClientCommissionPercentage: null,
            discountedMarkupPercentage: null,
            paymentOptionId,
            paymentOptionType,
            pointsToBurn,
            loyaltyRedeemId,
            analyticsEventType: null,
            isForGroups,
            locale
          };
          return await _getHotelsResults(paramsGetHotels).then(r => {
            if (r?.hotelResults?.length > 0) {
              dispatch?.(updateHotelResults({
                results: r.hotelResults?.map(r => ({
                  ...r,
                  _id: hr._id,
                  _hydrationInProgress: false,
                  _hydrationCompleted: true
                })),
                searchId
              }));
            } else {
              // não encontrei resultado na pesquisa do hotel - desmarcar _hydrationInProgress - ver #219395
              dispatch?.(updateHotelResults({
                results: [{
                  _id: hr._id,
                  _hydrationInProgress: false,
                  _hydrationCompleted: true
                }],
                searchId
              }));
            }
            return r?.hotelResults;
          });
        }();
      })).then(hotelResultsArrays => {
        const r: HotelResult[] = hotelResultsArrays.filter(Boolean).flat(1);
        callback(null, r);
        return r;
      }).catch(err => {
        errors.push(err);
        // deu erro na hidratação? atualiza _hydrationInProgress do resultado
        dispatch(updateHotelResults({
          results: [{
            _id: hr._id,
            _hydrationInProgress: false
          }],
          searchId
        }));
        callback(null, null);
        return null;
      });
    }
  });
  const concurrencyLimit = 10; // limite escolhido arbitrariamente para buscas em paralelo
  try {
    return await new Promise<SearchHotelsResponse>((res, rej) => {
      parallelLimit(asyncFunctions, concurrencyLimit, (err, results) => {
        const successes = results?.filter(Boolean);
        if (successes?.length > 0) {
          const errorMessage = errors?.length > 0 ? _uniq(errors.map(err => typeof err == 'string' ? err : err?.message ?? err?.errorMessage ?? "Erro desconhecido").filter(Boolean)).join(', ') : undefined;
          res({
            results: successes.flat(1),
            errorMessage,
            searchId,
            locale
          });
        } else if (errors?.length > 0) {
          rej(errors?.[0]);
        } else {
          res({
            results: [],
            searchId
          });
        }
      });
    }).then(r => {
      return r;
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return rejectWithValue({
      errorMessage: error?.message || error?.errorMessage || "Erro desconhecido",
      searchId
    });
  }
});